import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useDepartment } from '../../App';
import { format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, getWeek } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import './VacationCalendarPage.css';

const VacationCalendarPage = () => {
    const { selectedDepartment } = useDepartment();
    const [periodStart, setPeriodStart] = useState(startOfMonth(new Date()));
    const [selectedPersonId, setSelectedPersonId] = useState(null);

    const periodEnd = endOfMonth(periodStart);

    const { data: vacations, isLoading, isFetching } = useQuery(
        ['vacations', periodStart, periodEnd, selectedDepartment],
        async () => {
            await new Promise(resolve => setTimeout(resolve, 300));
            const params = {
                start_date: format(periodStart, 'yyyy-MM-dd'),
                end_date: format(periodEnd, 'yyyy-MM-dd')
            };
            if (selectedDepartment) {
                params.department_name = selectedDepartment;
            }
            const response = await axios.get(`${API_BASE_URL}/vacations/calendar`, { params });
            return response.data;
        },
        {
            keepPreviousData: true,
            staleTime: 0,
            cacheTime: 5 * 60 * 1000,
            refetchOnMount: 'always',
            refetchOnWindowFocus: true,
        }
    );

    const { data: departmentSettings } = useQuery(
        ['departmentSettings', selectedDepartment],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/department-settings`, {
                params: { department_name: selectedDepartment }
            });
            return response.data;
        },
        { enabled: !!selectedDepartment }
    );

    /** Лимит пересекающихся отпусков на одну дату (поле API исторически max_vacations_per_week) */
    const maxPerDay = departmentSettings?.max_vacations_per_week
        ? parseInt(departmentSettings.max_vacations_per_week)
        : 2;

    const days = useMemo(() => {
        return eachDayOfInterval({ start: periodStart, end: periodEnd });
    }, [periodStart, periodEnd]);

    const goToPreviousMonth = useCallback(() => {
        setPeriodStart(prev => addMonths(prev, -1));
    }, []);

    const goToNextMonth = useCallback(() => {
        setPeriodStart(prev => addMonths(prev, 1));
    }, []);

    const togglePersonSelection = useCallback((personId) => {
        setSelectedPersonId(prev => (prev === personId ? null : personId));
    }, []);

    const people = useMemo(() => {
        if (!vacations) return [];

        const peopleMap = new Map();

        vacations.forEach(vacation => {
            const key = vacation.full_name;
            if (!peopleMap.has(key)) {
                peopleMap.set(key, {
                    id: vacation.id,
                    name: vacation.full_name,
                    vacations: []
                });
            }
            peopleMap.get(key).vacations.push(vacation);
        });

        return Array.from(peopleMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [vacations]);

    // Сколько разных отпусков пересекают этот календарный день (по отделению уже отфильтровано API)
    const vacationsPerDay = useMemo(() => {
        if (!vacations) return new Map();

        const dayMap = new Map();

        vacations.forEach(vacation => {
            const startParts = vacation.start_date.split('T')[0].split('-');
            const endParts = vacation.end_date.split('T')[0].split('-');
            const start = new Date(startParts[0], startParts[1] - 1, startParts[2]);
            const end = new Date(endParts[0], endParts[1] - 1, endParts[2]);

            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                const dayKey = format(d, 'yyyy-MM-dd');
                if (!dayMap.has(dayKey)) {
                    dayMap.set(dayKey, new Set());
                }
                dayMap.get(dayKey).add(vacation.id);
            }
        });

        const result = new Map();
        dayMap.forEach((ids, dayKey) => {
            result.set(dayKey, ids.size);
        });
        return result;
    }, [vacations]);

    const isDayAtLimit = useCallback((day) => {
        const key = format(day, 'yyyy-MM-dd');
        return (vacationsPerDay.get(key) || 0) >= maxPerDay;
    }, [vacationsPerDay, maxPerDay]);

    const getPersonVacationForDay = useCallback((personVacations, day) => {
        if (!personVacations || personVacations.length === 0) return null;

        const dayStr = format(day, 'yyyy-MM-dd');

        return personVacations.find(vacation => {
            const startStr = vacation.start_date.split('T')[0];
            const endStr = vacation.end_date.split('T')[0];
            return dayStr >= startStr && dayStr <= endStr;
        });
    }, []);

    const getCellSpanForDay = useCallback((personVacations, dayIndex) => {
        const day = days[dayIndex];
        const vacation = getPersonVacationForDay(personVacations, day);

        if (!vacation) return null;

        if (dayIndex > 0) {
            const prevDay = days[dayIndex - 1];
            const prevVacation = getPersonVacationForDay(personVacations, prevDay);
            if (prevVacation && prevVacation.id === vacation.id) {
                return { skip: true };
            }
        }

        let span = 1;
        for (let i = dayIndex + 1; i < days.length; i++) {
            const nextVacation = getPersonVacationForDay(personVacations, days[i]);
            if (nextVacation && nextVacation.id === vacation.id) {
                span++;
            } else {
                break;
            }
        }

        return { span, vacation };
    }, [days, getPersonVacationForDay]);

    if (isLoading) {
        return (
            <div className="calendar-page">
                <div className="calendar-header">
                    <div>
                        <h1>
                            <CalendarIcon size={28} />
                            Календарь отпусков
                        </h1>
                        <p>Расписание отпусков сотрудников</p>
                    </div>
                </div>
                <div className="loading-container">
                    <div className="loading">
                        <div className="spinner"></div>
                        <p>Загрузка календаря...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="calendar-page">
            <div className="calendar-header">
                <div>
                    <h1>
                        <CalendarIcon size={28} />
                        Календарь отпусков
                        {isFetching && <span className="loading-indicator">🔄</span>}
                    </h1>
                    <p>{selectedDepartment ? `Отделение: ${selectedDepartment}` : ''}</p>
                </div>
                <div className="period-controls">
                    <button className="btn btn-secondary" onClick={goToPreviousMonth}>
                        <ChevronLeft size={18} />
                        Назад
                    </button>
                    <span className="period-label">
                        {format(periodStart, 'LLLL yyyy', { locale: ru })}
                    </span>
                    <button className="btn btn-secondary" onClick={goToNextMonth}>
                        Вперёд
                        <ChevronRight size={18} />
                    </button>
                </div>
            </div>

            <div className="calendar-table-container">
                {people.length === 0 ? (
                    <div className="empty-state">
                        <CalendarIcon size={64} className="empty-icon" />
                        <h3>Нет отпусков</h3>
                        <p>Отпуска пока не запланированы</p>
                    </div>
                ) : (
                    <table className="calendar-table">
                        <thead>
                            <tr>
                                <th className="person-column sticky-column">Сотрудник</th>
                                {days.map((day, idx) => {
                                    const dayOfWeek = day.getDay();
                                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                                    const weekNum = getWeek(day, { weekStartsOn: 1 });
                                    const isEvenWeek = weekNum % 2 === 0;
                                    const overloaded = isDayAtLimit(day);
                                    return (
                                        <th
                                            key={idx}
                                            className={`day-column ${isEvenWeek ? 'week-even' : 'week-odd'} ${isWeekend ? 'weekend' : ''} ${overloaded ? 'overloaded' : ''}`}
                                            title={overloaded ? `На эту дату уже ${maxPerDay} отпусков (лимит)` : ''}
                                        >
                                            <div className="day-header">
                                                {overloaded && <AlertTriangle size={10} className="overload-icon" />}
                                                <div className="day-number">{format(day, 'd')}</div>
                                                <div className="day-name">{format(day, 'EEE', { locale: ru })}</div>
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {people.map(person => (
                                <tr
                                    key={person.id}
                                    className={selectedPersonId === person.id ? 'selected-row' : ''}
                                    onClick={() => togglePersonSelection(person.id)}
                                >
                                    <td className="person-cell sticky-column">
                                        {person.name}
                                    </td>
                                    {days.map((day, dayIndex) => {
                                        const cellInfo = getCellSpanForDay(person.vacations, dayIndex);
                                        const dayOfWeek = day.getDay();
                                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                                        const weekNum = getWeek(day, { weekStartsOn: 1 });
                                        const isEvenWeek = weekNum % 2 === 0;

                                        if (cellInfo?.skip) {
                                            return null;
                                        }

                                        if (cellInfo?.vacation) {
                                            return (
                                                <td
                                                    key={dayIndex}
                                                    colSpan={cellInfo.span}
                                                    className="vacation-cell"
                                                    title={`${format(new Date(cellInfo.vacation.start_date), 'dd.MM.yyyy')} - ${format(new Date(cellInfo.vacation.end_date), 'dd.MM.yyyy')}`}
                                                >
                                                    <div className="vacation-content">
                                                        Отпуск
                                                    </div>
                                                </td>
                                            );
                                        }

                                        return <td key={dayIndex} className={`empty-cell ${isEvenWeek ? 'week-even' : 'week-odd'} ${isWeekend ? 'weekend' : ''}`}></td>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default VacationCalendarPage;
